import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { updateCompanyApi } from "../api";

const FIELDS = [
  { key: "name",            labelKey: "requisites.field_name",            type: "text",     adminOnly: true },
  { key: "legal_form",      labelKey: "requisites.field_legal_form",      type: "text" },
  { key: "bin",             labelKey: "requisites.field_bin",             type: "text",     phKey: "requisites.field_bin_ph" },
  { key: "activity",        labelKey: "requisites.field_activity",        type: "text" },
  { key: "director_name",   labelKey: "requisites.field_director_name",   type: "text" },
  { key: "address",         labelKey: "requisites.field_address",         type: "textarea" },
  { key: "actual_address",  labelKey: "requisites.field_actual_address",  type: "textarea" },
  { key: "phone",           labelKey: "requisites.field_phone",           type: "tel" },
  { key: "email_contact",   labelKey: "requisites.field_email_contact",   type: "email" },
  { key: "website",         labelKey: "requisites.field_website",         type: "url" },
  { key: "bank_name",       labelKey: "requisites.field_bank_name",       type: "text" },
  { key: "bank_iban",       labelKey: "requisites.field_bank_iban",       type: "text" },
  { key: "bank_bic",        labelKey: "requisites.field_bank_bic",        type: "text" },
  { key: "bank_kbe",        labelKey: "requisites.field_bank_kbe",        type: "text",     phKey: "requisites.field_bank_kbe_ph" },
  { key: "notes",           labelKey: "requisites.field_notes",           type: "textarea" },
];

export default function CompanyRequisitesModal({ open, onClose }) {
  const { t } = useTranslation();
  const { user, company, refresh } = useAuth();
  const canEdit = user?.role === "director" || user?.role === "hr";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (open) {
      setEditing(false);
      setErr(""); setMsg("");
      const initial = {};
      FIELDS.forEach(f => { initial[f.key] = company?.[f.key] || ""; });
      setForm(initial);
    }
  }, [open, company]);

  if (!open) return null;

  const onSave = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg("");
    try {
      await updateCompanyApi(form);
      setMsg(t("requisites.saved"));
      if (refresh) await refresh();
      setEditing(false);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || t("common.save_error"));
    } finally { setBusy(false); }
  };

  const renderValue = (v) => {
    if (!v) return <span className="text-muted fst-italic">{t("common.not_set")}</span>;
    return <span style={{ wordBreak: "break-word" }}>{v}</span>;
  };

  return (
    <div className="modal d-block" tabIndex="-1"
         style={{ background: "rgba(0,0,0,0.5)", overflowY: "auto" }}
         data-testid="requisites-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-dialog modal-dialog-centered modal-lg" style={{ marginTop: 24, marginBottom: 24 }}>
        <div className="modal-content" style={{ maxHeight: "calc(100vh - 48px)" }}>
          <div className="modal-header" style={{ flexShrink: 0 }}>
            <h5 className="modal-title">
              <i className="bi bi-card-text me-2"></i>
              {t("requisites.title")} · <span className="text-muted">{company?.name || "—"}</span>
            </h5>
            <button type="button" className="btn-close" onClick={onClose}
                    data-testid="requisites-close"></button>
          </div>

          <form onSubmit={onSave} style={{ display: "flex", flexDirection: "column", minHeight: 0, flexGrow: 1 }}>
            <div className="modal-body" style={{ overflowY: "auto", flexGrow: 1, padding: "1.25rem 1.5rem" }}>
              {err && <div className="alert alert-danger py-2">{err}</div>}
              {msg && <div className="alert alert-success py-2">{msg}</div>}

              {!company?.company_id && (
                <div className="alert alert-warning py-2">{t("requisites.no_company")}</div>
              )}

              {editing ? (
                <div className="row g-3 pb-1">
                  {FIELDS.map(f => (
                    (!f.adminOnly || canEdit) && (
                      <div className={f.type === "textarea" ? "col-12" : "col-md-6"} key={f.key}>
                        <label className="form-label small fw-semibold">{t(f.labelKey)}</label>
                        {f.type === "textarea" ? (
                          <textarea className="form-control" rows={2}
                                    value={form[f.key] || ""}
                                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                    data-testid={`req-${f.key}`} />
                        ) : (
                          <input type={f.type} className="form-control"
                                 placeholder={f.phKey ? t(f.phKey) : ""}
                                 value={form[f.key] || ""}
                                 onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                 data-testid={`req-${f.key}`} />
                        )}
                      </div>
                    )
                  ))}
                </div>
              ) : (
                <table className="table table-sm mb-0">
                  <tbody>
                    {FIELDS.map(f => (
                      <tr key={f.key} data-testid={`req-row-${f.key}`}>
                        <td className="text-muted" style={{ width: "40%" }}>{t(f.labelKey)}</td>
                        <td className="fw-semibold">{renderValue(company?.[f.key])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="modal-footer" style={{ flexShrink: 0, padding: "1rem 1.5rem", gap: 8 }}>
              {canEdit && !editing && company?.company_id && (
                <button type="button" className="btn btn-primary"
                        onClick={() => setEditing(true)}
                        data-testid="requisites-edit-btn">
                  <i className="bi bi-pencil me-1"></i>{t("requisites.edit")}
                </button>
              )}
              {editing && (
                <>
                  <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>
                    {t("common.cancel")}
                  </button>
                  <button type="submit" className="btn btn-success" disabled={busy}
                          data-testid="requisites-save-btn">
                    <i className="bi bi-check-lg me-1"></i>{t("common.save")}
                  </button>
                </>
              )}
              {!editing && (
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                  {t("common.close")}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
